/**
 * spiceVisitPrefill - the sessionStorage handoff from a client-scoped
 * door ("Write the SPICE email summary" on My week, My clients or the
 * attention band; the client record holds no such door since LP-28) into
 * the SPICE email drafter, which is otherwise a client-neutral tool
 * page.
 *
 * Mirrors notePrefill.ts's handoff shape: the client id travels in the
 * URL (an opaque identifier, exactly like every /clients/{clientId}
 * route already in this app), and the narrative-adjacent text (the
 * client's display name, the last session's plan) travels here instead
 * of the query string.
 */

export interface SpiceVisitPrefill {
  client_display_name: string;
  /** The last SPICE-band note's plan line, when one exists yet. */
  last_plan: string | null;
  /**
   * The client's emergency contact's or POA's first name, when a door
   * already holds decrypted contact facts (a record-scoped door,
   * arriving from an aggregate that has already read them for the page
   * it's on) — never a new decrypting read from a client-neutral page
   * like this one. Optional on write (every door writing here today has
   * no contact fact to offer; each says so at its own call site) so an
   * existing caller stays untouched; always present once stored.
   */
  contact_first_name: string | null;
  saved_at: number;
}

/** Unconsumed prefills die after 15 minutes (notePrefill.ts precedent):
 *  navigation is immediate, so anything older is a stranded handoff. */
export const SPICE_VISIT_PREFILL_TTL_MS = 15 * 60 * 1000;

export function spiceVisitPrefillKey(tenantSlug: string, clientId: string): string {
  return `spice-visit-prefill:${tenantSlug}:${clientId}`;
}

export function writeSpiceVisitPrefill(
  tenantSlug: string,
  clientId: string,
  prefill: Omit<SpiceVisitPrefill, 'saved_at' | 'contact_first_name'> & { contact_first_name?: string | null },
): void {
  try {
    const stored: SpiceVisitPrefill = {
      ...prefill,
      contact_first_name: prefill.contact_first_name ?? null,
      saved_at: Date.now(),
    };
    window.sessionStorage.setItem(spiceVisitPrefillKey(tenantSlug, clientId), JSON.stringify(stored));
  } catch {
    // sessionStorage unavailable (private mode / quota) - the door still
    // navigates; the drafter just opens without the plan prefilled.
  }
}

/** Read the prefill WITHOUT clearing it (peek + a separate clear keeps
 *  mount-time consumption idempotent under React double-render). */
export function peekSpiceVisitPrefill(tenantSlug: string, clientId: string): SpiceVisitPrefill | null {
  const key = spiceVisitPrefillKey(tenantSlug, clientId);
  try {
    const raw = window.sessionStorage.getItem(key);
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    if (
      parsed !== null &&
      typeof parsed === 'object' &&
      typeof (parsed as SpiceVisitPrefill).client_display_name === 'string' &&
      (parsed as SpiceVisitPrefill).client_display_name.length > 0 &&
      typeof (parsed as SpiceVisitPrefill).saved_at === 'number' &&
      Date.now() - (parsed as SpiceVisitPrefill).saved_at <= SPICE_VISIT_PREFILL_TTL_MS
    ) {
      const p = parsed as SpiceVisitPrefill;
      return {
        client_display_name: p.client_display_name,
        last_plan: typeof p.last_plan === 'string' && p.last_plan.length > 0 ? p.last_plan : null,
        contact_first_name:
          typeof p.contact_first_name === 'string' && p.contact_first_name.length > 0
            ? p.contact_first_name
            : null,
        saved_at: p.saved_at,
      };
    }
    window.sessionStorage.removeItem(key);
    return null;
  } catch {
    try {
      window.sessionStorage.removeItem(key);
    } catch {
      // ignore
    }
    return null;
  }
}

export function clearSpiceVisitPrefill(tenantSlug: string, clientId: string): void {
  try {
    window.sessionStorage.removeItem(spiceVisitPrefillKey(tenantSlug, clientId));
  } catch {
    // ignore
  }
}
